import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';

/**
 * CopyButton
 * ----------
 * Copy one plain value to the clipboard — the Admin Handover screen's copy
 * affordance (TDI-386: every value EMIS asks for has a copy button), shared so
 * every RMS surface copies the same way. First shared copy control in the
 * repo; the inline ones (RmsProviderAccountsTable, PatientInfoPanel) predate
 * it.
 *
 * Copies the untrimmed plain value it is given, never the formatted display
 * next to it. Confirms with a brief tick and a toast; falls back to
 * execCommand where the Clipboard API is unavailable (older WebViews). The
 * button is disabled without a value, so "Not recorded" can never be pasted
 * into EMIS as if it were data.
 *
 * @param {{ value: ?string, label: string, className?: string }} props
 *   label names what is copied, e.g. "Copy the NHS number".
 */
export default function CopyButton({ value, label, className = '' }) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleCopy = useCallback(async () => {
    const text = value == null ? '' : String(value);
    if (!text) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch (_err) {
      // Clipboard API unavailable or blocked: legacy textarea fallback.
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        ok = document.execCommand('copy');
        textarea.remove();
      } catch (_fallbackErr) {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Select the value and copy it manually.');
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!value}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium rounded-md border transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
        ${!value
          ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
          : copied
            ? 'border-green-300 bg-green-50 text-green-700'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
        } ${className}`}
    >
      {copied ? (
        <CheckIcon className="w-4 h-4" aria-hidden="true" />
      ) : (
        <DocumentDuplicateIcon className="w-4 h-4" aria-hidden="true" />
      )}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}
